/* Dovanų kortelės ir nuolaidos kodo laukelis krepšelyje: kodas pridedamas prie to paties krepšelio per
   Storefront API (cartGiftCardCodesAdd, cartDiscountCodesUpdate), todėl kasa atsidaro su jau pritaikytu kodu.
   Asmeniniai kodai (laimės rato, segmentų) krepšelyje dar negalioja, nes pirkėjas nežinomas: juos paliekam
   krepšelyje, o Shopify patikrina kasoje. Veikia ir drawer'yje, ir
   krepšelio puslapyje; drawer'iui persikrovus elementas susikuria iš naujo ir pasikrauna būseną. */
if (!customElements.get('gift-card-field')) {
  const API = '/api/2025-07/graphql.json';
  const CART_FIELDS = 'discountCodes { code applicable } appliedGiftCards { id lastCharacters amountUsed { amount currencyCode } balance { amount currencyCode } } cost { totalAmount { amount currencyCode } }';

  class GiftCardField extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('.js-gc-input');
      this.button = this.querySelector('.js-gc-apply');
      this.msg = this.querySelector('.js-gc-msg');
      this.list = this.querySelector('.js-gc-applied');
      this.button.addEventListener('click', () => this.apply());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.apply();
        }
      });
      this.load();
    }

    async gql(query, variables) {
      const res = await fetch(API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.dataset.token
        },
        body: JSON.stringify({ query, variables })
      });
      const json = await res.json();
      if (json.errors) throw new Error(json.errors[0].message);
      return json.data;
    }

    async cartId() {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      return `gid://shopify/Cart/${cart.token}`;
    }

    money(m) {
      return new Intl.NumberFormat('lt-LT', { style: 'currency', currency: m.currencyCode }).format(Number(m.amount));
    }

    say(text, error = false) {
      this.msg.textContent = text;
      this.msg.classList.toggle('is-error', error);
      this.msg.hidden = !text;
    }

    render(cart) {
      const cards = (cart && cart.appliedGiftCards) || [];
      const codes = (cart && cart.discountCodes) || [];
      this.list.innerHTML = '';
      cards.forEach((c) => {
        const li = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = `Dovanų kortelė •••• ${c.lastCharacters.toUpperCase()}: −${this.money(c.amountUsed)}`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'gift-card-field__remove';
        remove.textContent = 'Pašalinti';
        remove.addEventListener('click', () => this.remove(c.id));
        li.append(label, remove);
        this.list.append(li);
      });
      codes.forEach((d) => {
        const li = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = d.applicable ? `Nuolaidos kodas ${d.code}` : `Nuolaidos kodas ${d.code} (bus tikrinamas kasoje)`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'gift-card-field__remove';
        remove.textContent = 'Pašalinti';
        remove.addEventListener('click', () => this.removeCode(d.code));
        li.append(label, remove);
        this.list.append(li);
      });
      if (cards.length || codes.some((d) => d.applicable)) {
        const due = document.createElement('li');
        due.className = 'gift-card-field__due';
        // totalAmount jau su atimta kortele (checkoutChargeAmount jos neatima). Pristatymas pridedamas kasoje.
        const a = document.createElement('span');
        a.textContent = 'Mokėti liks (be pristatymo)';
        const b = document.createElement('span');
        b.textContent = this.money(cart.cost.totalAmount);
        due.append(a, b);
        this.list.append(due);
      }
      this.list.hidden = !cards.length && !codes.length;
    }

    async load() {
      try {
        const id = await this.cartId();
        const data = await this.gql(`query($id: ID!) { cart(id: $id) { ${CART_FIELDS} } }`, { id });
        this.render(data.cart);
      } catch (e) {
        // Būsenos nepavyko gauti: laukelis lieka, kortelę vis tiek galima įvesti kasoje.
      }
    }

    async apply() {
      const code = this.input.value.replace(/\s+/g, '');
      if (!code) {
        this.say('Įveskite dovanų kortelės arba nuolaidos kodą.', true);
        return;
      }
      this.button.disabled = true;
      this.say('Tikriname kodą…');
      try {
        const id = await this.cartId();
        const before = await this.gql(`query($id: ID!) { cart(id: $id) { appliedGiftCards { id } discountCodes { code } } }`, { id });
        const data = await this.gql(
          `mutation($id: ID!, $codes: [String!]!) { cartGiftCardCodesAdd(cartId: $id, giftCardCodes: $codes) { cart { ${CART_FIELDS} } userErrors { message } } }`,
          { id, codes: [code] }
        );
        const r = data.cartGiftCardCodesAdd;
        const added = r.cart && r.cart.appliedGiftCards.length > before.cart.appliedGiftCards.length;
        if (r.userErrors.length || !added) {
          await this.applyDiscount(id, code, before.cart.discountCodes.map((d) => d.code));
        } else {
          this.input.value = '';
          this.say('Dovanų kortelė pritaikyta. Suma bus nurašyta kasoje.');
          this.render(r.cart);
        }
      } catch (e) {
        this.say('Nepavyko pritaikyti. Kodą galite įvesti kasoje.', true);
      } finally {
        this.button.disabled = false;
      }
    }

    async applyDiscount(id, code, existing) {
      const data = await this.setCodes(id, [...existing, code]);
      const found = data.discountCodes.find((d) => d.code.toLowerCase() === code.toLowerCase());
      this.input.value = '';
      this.render(data);
      if (found && found.applicable) {
        this.say('Nuolaidos kodas pritaikytas.');
        this.refreshTotals();
      } else {
        this.say('Dabar kodo patikrinti negalime. Jei jis galioja, nuolaida bus pritaikyta kasoje, įvedus el. paštą.', true);
      }
    }

    async setCodes(id, codes) {
      const data = await this.gql(
        `mutation($id: ID!, $codes: [String!]!) { cartDiscountCodesUpdate(cartId: $id, discountCodes: $codes) { cart { ${CART_FIELDS} } userErrors { message } } }`,
        { id, codes }
      );
      return data.cartDiscountCodesUpdate.cart;
    }

    async removeCode(code) {
      try {
        const id = await this.cartId();
        const now = await this.gql(`query($id: ID!) { cart(id: $id) { discountCodes { code } } }`, { id });
        const cart = await this.setCodes(id, now.cart.discountCodes.map((d) => d.code).filter((c) => c !== code));
        this.say('');
        this.render(cart);
        this.refreshTotals();
      } catch (e) {
        this.say('Nepavyko pašalinti, bandykite dar kartą.', true);
      }
    }

    // Nuolaida keičia krepšelio sumas, tad jas reikia perkrauti (kortelė jų nekeičia).
    refreshTotals() {
      if (this.closest('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      } else {
        window.setTimeout(() => window.location.reload(), 900);
      }
    }

    async remove(giftCardId) {
      try {
        const id = await this.cartId();
        const data = await this.gql(
          `mutation($id: ID!, $ids: [ID!]!) { cartGiftCardCodesRemove(cartId: $id, appliedGiftCardIds: $ids) { cart { ${CART_FIELDS} } userErrors { message } } }`,
          { id, ids: [giftCardId] }
        );
        this.say('');
        this.render(data.cartGiftCardCodesRemove.cart);
      } catch (e) {
        this.say('Nepavyko pašalinti, bandykite dar kartą.', true);
      }
    }
  }
  customElements.define('gift-card-field', GiftCardField);
}
